/**
 * ⛔⛔ IS THIS RESEARCH ERROR A PERSONA REFUSAL THE OPERATOR CAN ACTUALLY RESOLVE?
 *
 * The reconcile button is shown only when the persisted `research_error` is one of the two
 * `preserveBenefitWeights` refusals. That makes the BUTTON'S REACHABILITY DEPEND ON A TEXT MATCH
 * against a message written in another file, in another language, by another change.
 *
 * 🔑 THAT IS THE FAILURE MODE OF THIS WHOLE CONTROL. If the guard's wording is edited and this
 * predicate is not, the refusal still renders, the button silently stops appearing, and the
 * capability becomes unreachable while every test still passes — the EnrichmentReview regenerate
 * button all over again, which read as present in the source and had no reachable instances.
 * ⇒ So the anchors below are pinned by a test that reads the REAL strings out of
 * `preserveBenefitWeights.js`. Change the guard's wording and that test goes red, here, before it
 * reaches an operator.
 *
 * ⚠️ ANCHORED ON THE STABLE CLAUSE, NOT THE WHOLE SENTENCE. Both refusals carry a long explanatory
 * body that is genuinely likely to be reworded; the clause naming the mechanism is what has survived
 * every revision of that file. Matching the whole sentence would break on a typo fix.
 */

/** The stranded refusal: stored weights whose persona_key has no successor in the new research. */
const STRANDED_ANCHOR = /carry\s+benefit_weights\s+that\s+this\s+research\s+would\s+strand/i;

/** The conflicting refusal: one persona_key on two stored personas with different weights. */
const CONFLICTING_ANCHOR = /appear\s+on\s+MORE\s+THAN\s+ONE\s+stored\s+persona\s+with\s+DIFFERENT\s+benefit_weights/i;

export type PersonaRefusalKind = 'stranded' | 'conflicting' | null;

/**
 * Which persona refusal, if any, this error text is.
 *
 * ⚠️ Returns the KIND rather than a boolean, because the two are different situations for the
 * operator: stranded means "choose where these weights go", conflicting means "the stored data was
 * already ambiguous before your refresh". The panel says so, and a boolean could not.
 */
export function personaRefusalKind(errorText: unknown): PersonaRefusalKind {
  if (typeof errorText !== 'string' || errorText.trim() === '') return null;
  if (STRANDED_ANCHOR.test(errorText)) return 'stranded';
  if (CONFLICTING_ANCHOR.test(errorText)) return 'conflicting';
  return null;
}

/**
 * ⛔ Reconciliation resolves STRANDED keys. A conflicting-key refusal is about the STORED data being
 * ambiguous before the refresh ran, and the reconcile panel can offer a winner-pick for it — but the
 * two must not be conflated in the copy, because "choose where these weights go" is meaningless
 * advice for a duplicate key.
 */
export function canReconcile(errorText: unknown): boolean {
  return personaRefusalKind(errorText) !== null;
}

/** Exported for the pinning test, so the anchors are checkable against the guard's real source. */
export const REFUSAL_ANCHORS = { STRANDED_ANCHOR, CONFLICTING_ANCHOR };

/**
 * ⛔⛔ IS RECONCILIATION REACHABLE FOR THIS CATEGORY *BEFORE* ANY REFUSAL HAS BEEN SEEN?
 *
 * `personaRefusalKind` answers "did a refusal happen and get PERSISTED". That is not the same
 * question as "can this operator reconcile", and treating them as one made the control
 * UNREACHABLE IN PRACTICE:
 *
 *   · `CategoryCharacteristicsService:2436` — on a preservation refusal the row is RESTORED to its
 *     prior state and `research_error` is set to NULL, because nothing was written and marking a
 *     healthy row `failed` would be a lie. That restore is CORRECT.
 *   · `personaRefusalKind` then reads that NULL and reports "no refusal", so the panel never mounts.
 *
 * 🔑 TWO DESIGNS THAT ARE EACH RIGHT ALONE, COMPOSING INTO A CAPABILITY NOBODY CAN REACH. Neither
 * side is buggy in isolation, which is why every test on both sides passed. MEASURED 2026-09-08:
 * ZERO of 56 full-path categories in sectors 72 and 84 carried a persisted refusal, so the button
 * appeared on none of them — including the one an operator had just been refused on, minutes earlier.
 *
 * ⇒ REACHABILITY IS A PROPERTY OF THE STORED DATA, NOT OF AN ERROR RECORD. A refresh WILL refuse
 * whenever a stored persona carries non-empty `benefit_weights` and the research renames its key —
 * so the presence of stored weights is the honest precondition for OFFERING the control. The
 * preview then establishes what actually needs deciding; this predicate only decides whether the
 * operator is allowed to ask.
 *
 * ⚠️ DELIBERATELY BROADER THAN THE REFUSAL. Some categories with stored weights would reconcile
 * cleanly (every key survives). Offering the panel there costs a preview, which writes NOTHING —
 * whereas withholding it costs the operator the only route out of a refusal. The asymmetry is the
 * whole argument: a false offer is a wasted click, a false withholding is an unreachable feature.
 */
export function personaWeightsAtRisk(personas: unknown): boolean {
  if (!Array.isArray(personas)) return false;
  return personas.some((p) => {
    if (!p || typeof p !== 'object') return false;
    const bw = (p as Record<string, unknown>).benefit_weights;
    return !!bw && typeof bw === 'object' && !Array.isArray(bw)
      && Object.keys(bw as Record<string, unknown>).length > 0;
  });
}
